import React, { useEffect, useMemo, useRef, useState } from 'react';
import { groupRegistrationsByAge, parseAgeFromRegRow } from './registrations/registrationUtils';

// ─── Linking helpers ────────────────────────────────────────────────────────────
function normalizeEmail(e) { return String(e || '').toLowerCase().trim(); }
function normalizeName(n) { return String(n || '').toLowerCase().trim().replace(/\s+/g, ' '); }
function buildPersonKey(email, name) { return `${normalizeEmail(email)}|${normalizeName(name)}`; }
function getRegKey(r) {
  const email = r?.email || r?.Email || '';
  const rawName = (r?.firstName || r?.lastName)
    ? `${r?.firstName || ''} ${r?.lastName || ''}`.trim()
    : (r?.Name || r?.name || '');
  return buildPersonKey(email, rawName);
}

export default function AdminRegistrations({
  token,

  // UI + fetch
  loading,
  error,
  fetchRegistrations,

  // normalized registrations array from Admin.jsx
  registrations
}) {
  // ------------------------
  // Local UI state (registrations-only)
  // ------------------------
  const [query, setQuery] = useState('');
  const groupBy = 'age';

  const GROUP_LABELS = useMemo(
    () => ['Kids 0-12', 'Youth 13-18', 'College 19-22', 'Young Adults 23-30', 'Adults 31+', 'Unknown'],
    []
  );

  const [selectedGroupView, setSelectedGroupView] = useState('All groups');

  // collapsed state per group/subgroup (keys are strings)
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Start everything collapsed by default
  const [allCollapsed, setAllCollapsed] = useState(true);

  // Ref to ensure we only initialize collapsedGroups once (prevents overwriting user toggles)
  const initCollapsedRef = useRef(false);

  // Print preview modal state (no popups)
  const [printModal, setPrintModal] = useState({ open: false, reg: null });

  // View (full registration) modal state
  const [viewModal, setViewModal] = useState({ open: false, reg: null, payload: null });

  // Per-row confirmation email state keyed by _rowIndex (or fallback getRegKey)
  // shape: { [key]: { loading, sentAt, error } }
  const [confirmStatus, setConfirmStatus] = useState({});

  // Toggle raw JSON visibility inside the View modal
  const [showRawJson, setShowRawJson] = useState(false);

  // ------------------------
  // Assignments linking
  // ------------------------
  const [assignmentsError, setAssignmentsError] = useState('');
  const [assignmentMap, setAssignmentMap] = useState(new Map());

  // Assignment filter: 'All' | 'Assigned' | 'Unassigned'
  const [assignmentFilter, setAssignmentFilter] = useState('All');

  useEffect(() => {
    if (!token) return;
    fetch('/.netlify/functions/admin-get-assignments', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        const rows = data.rows || [];
        const map = new Map();
        for (const row of rows) {
          const key = buildPersonKey(row.camperEmail, row.camperName);
          if (!map.has(key)) {
            map.set(key, { area: String(row.area || ''), room: String(row.room || '') });
          }
        }
        setAssignmentMap(map);
      })
      .catch((e) => setAssignmentsError(String(e?.message || e)));
  }, [token]);

  // ------------------------
  // Confirmation email status – initialize from sheet data
  // ------------------------
  const getRowKey = (r) => r._rowIndex != null ? String(r._rowIndex) : getRegKey(r);

  useEffect(() => {
    if (!registrations || !registrations.length) return;
    setConfirmStatus((prev) => {
      const next = { ...prev };
      registrations.forEach((r) => {
        const key = getRowKey(r);
        const sentAt = r.confirmationSentAt || r['confirmationSentAt'] || '';
        if (sentAt && !next[key]?.sentAt) {
          next[key] = { ...(next[key] || {}), loading: false, sentAt: String(sentAt), error: '' };
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations]);

  const sendConfirmation = async (r) => {
    const key = getRowKey(r);
    const email = r.email || r.Email || '';
    const firstName = r.firstName || '';
    const lastName = r.lastName || '';
    const rowIndex = r._rowIndex;

    if (!email) {
      setConfirmStatus((prev) => ({ ...prev, [key]: { loading: false, sentAt: '', error: 'No email address on file.' } }));
      return;
    }

    setConfirmStatus((prev) => ({ ...prev, [key]: { loading: true, sentAt: '', error: '' } }));

    try {
      const res = await fetch('/.netlify/functions/admin-send-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rowIndex, email, firstName, lastName })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setConfirmStatus((prev) => ({ ...prev, [key]: { loading: false, sentAt: data.confirmationSentAt || new Date().toISOString(), error: '' } }));
    } catch (e) {
      setConfirmStatus((prev) => ({ ...prev, [key]: { loading: false, sentAt: '', error: String(e?.message || e) } }));
    }
  };

  // ------------------------
  // Filter + group
  // ------------------------
  const filtered = useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    let list = registrations || [];
    if (q) {
      list = list.filter((r) => {
        const hay = [
          r.firstName,
          r.lastName,
          r.Name,
          r.name,
          r.email,
          r.Email,
          r.phone,
          r['Phone Number']
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    if (assignmentFilter === 'Assigned') {
      list = list.filter((r) => assignmentMap.has(getRegKey(r)));
    } else if (assignmentFilter === 'Unassigned') {
      list = list.filter((r) => !assignmentMap.has(getRegKey(r)));
    }
    return list;
  }, [registrations, query, assignmentFilter, assignmentMap]);

  const groupedByAge = useMemo(() => {
    if (groupBy !== 'age') return null;
    return groupRegistrationsByAge(filtered);
  }, [filtered, groupBy]);


  // ------------------------
  // Collapse init + reset
  // ------------------------
  useEffect(() => {
    // When we get a new dataset, re-initialize collapsed state
    initCollapsedRef.current = false;
  }, [registrations]);

  useEffect(() => {
    if (!groupedByAge) {
      setCollapsedGroups({});
      setAllCollapsed(true);
      return;
    }
    if (initCollapsedRef.current) return;

    const next = {};
    Object.keys(groupedByAge).forEach((groupLabel) => {
      next[groupLabel] = true;

      if (
        groupLabel === 'Youth 13-18' &&
        typeof groupedByAge[groupLabel] === 'object' &&
        !Array.isArray(groupedByAge[groupLabel])
      ) {
        const sub = groupedByAge[groupLabel];
        Object.keys(sub).forEach((subLabel) => {
          next[`${groupLabel}::${subLabel}`] = true;
        });
      }
    });

    setCollapsedGroups(next);
    setAllCollapsed(true);
    initCollapsedRef.current = true;
  }, [groupedByAge]);

  const isCollapsed = (key) =>
    Object.prototype.hasOwnProperty.call(collapsedGroups, key) ? !!collapsedGroups[key] : true;

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : true;
      return { ...prev, [key]: !current };
    });
  };

  const toggleAllGroups = () => {
    const collapse = !allCollapsed;
    const next = {};

    if (groupedByAge) {
      Object.keys(groupedByAge).forEach((groupLabel) => {
        next[groupLabel] = collapse;
        if (
          groupLabel === 'Youth 13-18' &&
          typeof groupedByAge[groupLabel] === 'object' &&
          !Array.isArray(groupedByAge[groupLabel])
        ) {
          const sub = groupedByAge[groupLabel];
          Object.keys(sub).forEach((subLabel) => {
            next[`${groupLabel}::${subLabel}`] = collapse;
          });
        }
      });
    } else {
      Object.keys(collapsedGroups).forEach((k) => {
        next[k] = collapse;
      });
    }

    setCollapsedGroups(next);
    setAllCollapsed(collapse);
  };

  // ----------
  // View modal helpers (pretty display)
  // ----------
  const formatValue = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) return v.map(String).join(', ');
    return String(v);
  };

  const renderKeyValueGrid = (entries) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 10
      }}
    >
      {entries
        .filter((e) => e && e.value !== '' && e.value !== undefined && e.value !== null)
        .map((e) => (
          <div
            key={e.label}
            style={{
              border: '1px solid #eee',
              borderRadius: 10,
              padding: 10,
              background: '#fff',
              minWidth: 0
            }}
          >
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{e.label}</div>
            <div style={{ fontWeight: 700, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {formatValue(e.value)}
            </div>
          </div>
        ))}
    </div>
  );


  // ------------------------
  // Helpers for printable view
  // ------------------------
  const safe = (v) => String(v ?? '').trim();

  const getRawPayloadObject = (r) => {
    const raw =
      r?.rawPayload ??
      r?.raw_payload ??
      r?.RawPayload ??
      r?.['Raw Payload'] ??
      '';

    if (!raw) return null;

    // If it's already an object, return it
    if (typeof raw === 'object') return raw;

    const s = String(raw).trim();
    if (!s) return null;

    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const boolYesNo = (v) => (v ? 'Yes' : 'No');

  const fmtPhone = (s) => {
    const d = String(s || '').replace(/\D/g, '');
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    return String(s || '');
  };

  const fmtDate = (s) => {
    const v = String(s || '').trim();
    if (!v) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  };

  const getRegFullName = (r) =>
    safe(`${r?.firstName || ''} ${r?.lastName || ''}`) || safe(r?.Name) || safe(r?.name) || 'Registration';

  const getRegDOB = (r) => safe(r?.dob || r?.DOB || r?.['Date of Birth']);
  const getHasAllergies = (r) => safe(r?.hasAllergies);
  const getAllergies = (r) => safe(r?.allergies);

  const getTakesMeds = (r) => safe(r?.takesMedications || r?.takesMeds || r?.takesMed);
  const getMedications = (r) => safe(r?.medications);

  const getSignature = (r) => safe(r?.signature || r?.policySignature);
  const getRelationship = (r) => safe(r?.relationship || r?.policyRelationship);
  const getSignedAt = (r) => safe(r?.dateTime || r?.signedAt || r?.policyDateTime);

  const getAgreements = (r) => (r && typeof r.agreements === 'object' && r.agreements ? r.agreements : null);

  const POLICY_LABELS = {
    assumptionRisk: 'Assumption of Risk',
    medicalAuth: 'Medical Authorization',
    codeOfConduct: 'Code of Conduct',
    emergency: 'Emergency / Safety',
    transportation: 'Transportation',
    background: 'Background Check',
    photo: 'Photo Consent'
  };

  const toPolicyLabel = (key) => {
    if (!key) return '';
    if (POLICY_LABELS[key]) return POLICY_LABELS[key];

    // fallback: "camelCaseKey" -> "Camel Case Key"
    const s = String(key)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .trim();

    return s ? s.charAt(0).toUpperCase() + s.slice(1) : String(key);
  };

  const toYesNo = (v) => {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (typeof v === 'string') {
      const s = v.toLowerCase().trim();
      if (s === 'true' || s === 'yes' || s === 'y') return 'Yes';
      if (s === 'false' || s === 'no' || s === 'n') return 'No';
    }
    return v ? 'Yes' : 'No';
  };

  const renderTable = (list) => (
    <div
      style={{
        overflowX: 'auto',
        background: '#fff',
        borderRadius: 8,
        padding: 6,
        boxShadow: '0 0 0 1px #f0f4f8 inset'
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left' }}>
            <th style={{ padding: '8px 12px' }}>First Name</th>
            <th style={{ padding: '8px 12px' }}>Last Name</th>
            <th style={{ padding: '8px 12px' }}>Phone</th>
            <th style={{ padding: '8px 12px' }}>Age</th>
            <th style={{ padding: '8px 12px' }}>Room</th>
            <th style={{ padding: '8px 12px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r, idx) => {
            const age = parseAgeFromRegRow(r);
            const info = assignmentMap.get(getRegKey(r)) || null;
            const key = getRowKey(r);
            const cs = confirmStatus[key] || {};
            const firstName = (r.firstName || r.FirstName || '').trim();
            const lastName = (r.lastName || r.LastName || '').trim();
            return (
              <tr
                key={r._id || r._rowIndex || r.email || idx}
                style={{
                  borderTop: '1px solid #eee',
                  background: (() => {
                    // handicap (your sheet column is "Handicap")
                    const handicapVal = String(r?.['Handicap'] ?? '').toLowerCase().trim();
                    const wantsHandicap =
                      handicapVal === 'yes' || handicapVal === 'true' || handicapVal === 'y' || handicapVal === '1' || handicapVal === 'requested';

                    // sponsorship (highlight if Sponsorship Details has any text)
                    const sponsorshipDetails = String(r?.['Sponsorship Details'] ?? '').trim();
                    const wantsSponsorship = sponsorshipDetails.length > 0;

                    // sponsorship wins if both are true
                    if (wantsSponsorship) return '#fff7ed'; // light orange
                    if (wantsHandicap) return '#fff5f5';    // light red
                    if (!info) return '#fffbeb';            // light yellow for unassigned
                    return '#fff';
                  })()
                }}
              >
                <td style={{ padding: '12px' }}>
                  {firstName}
                  {String(r?.['Sponsorship Details'] || '').trim() ? (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        fontWeight: 800,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: '#ffedd5',
                        color: '#9a3412',
                        border: '1px solid #fed7aa'
                      }}
                    >
                      Sponsorship
                    </span>
                  ) : null}
                </td>
                <td style={{ padding: '12px' }}>{lastName}</td>
                <td style={{ padding: '12px' }}>{r.phone || r['Phone Number'] || ''}</td>
                <td style={{ padding: '12px' }}>{age === null || age === undefined ? '' : age}</td>
                <td style={{ padding: '12px', color: info ? '#1e3a5f' : '#9ca3af' }}>
                  {info ? `${info.area} / ${info.room}` : '—'}
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      className="btn"
                      type="button"
                      onClick={() => {
                        setShowRawJson(false);
                        setViewModal({ open: true, reg: r, payload: getRawPayloadObject(r) });
                      }}
                    >
                      View
                    </button>

                    <button
                      className="btn"
                      type="button"
                      onClick={() => setPrintModal({ open: true, reg: r })}
                    >
                      Print
                    </button>

                    {/* Confirmation email button */}
                    {cs.sentAt ? (
                      <span style={{ fontSize: 12, color: '#16a34a', whiteSpace: 'nowrap' }}>
                        ✓ Sent {new Date(cs.sentAt).toLocaleString()}
                      </span>
                    ) : (
                      <button
                        className="btn"
                        type="button"
                        disabled={cs.loading}
                        onClick={() => sendConfirmation(r)}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {cs.loading ? 'Sending…' : 'Email'}
                      </button>
                    )}
                    {cs.error ? (
                      <span style={{ fontSize: 12, color: '#dc2626', display: 'block', width: '100%' }}>
                        ⚠ {cs.error}
                      </span>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderGroupBlock = (groupLabel, groupVal) => {
    // Youth subgroup special rendering
    if (groupLabel === 'Youth 13-18' && typeof groupVal === 'object' && !Array.isArray(groupVal)) {
      const groupKey = groupLabel;
      const groupCollapsed = isCollapsed(groupKey);
      const total = Object.keys(groupVal).reduce((s, k) => s + (groupVal[k] ? groupVal[k].length : 0), 0);

      return (
        <div key={groupLabel} style={{ marginBottom: 20 }}>
          <div
            onClick={() => toggleGroup(groupKey)}
            style={{
              cursor: 'pointer',
              fontWeight: 700,
              margin: '6px 0',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <span style={{ fontSize: 14 }}>
              {groupLabel} ({total})
            </span>
            <span style={{ fontSize: 12, color: '#666' }}>{groupCollapsed ? '▸' : '▾'}</span>
          </div>

          {!groupCollapsed && (
            <div
              style={{
                borderRadius: 8,
                background: '#fff',
                padding: 8,
                boxShadow: '0 0 0 1px #f0f4f8 inset'
              }}
            >
              {Object.keys(groupVal).map((subLabel) => {
                const subKey = `${groupKey}::${subLabel}`;
                const subCollapsed = isCollapsed(subKey);
                const list = groupVal[subLabel] || [];

                return (
                  <div key={subLabel} style={{ marginBottom: 12 }}>
                    <div
                      onClick={() => toggleGroup(subKey)}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 4px'
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        {subLabel} ({list.length})
                      </div>
                      <div style={{ color: '#666' }}>{subCollapsed ? '▸' : '▾'}</div>
                    </div>

                    {!subCollapsed && renderTable(list)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Normal groups
    const list = Array.isArray(groupVal) ? groupVal : [];
    const groupKey = groupLabel;
    const groupCollapsed = isCollapsed(groupKey);

    return (
      <div key={groupLabel} style={{ marginBottom: 20 }}>
        <div
          onClick={() => toggleGroup(groupKey)}
          style={{
            cursor: 'pointer',
            fontWeight: 700,
            margin: '6px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span style={{ fontSize: 14 }}>
            {groupLabel} ({list.length})
          </span>
          <span style={{ fontSize: 12, color: '#666' }}>{groupCollapsed ? '▸' : '▾'}</span>
        </div>

        {!groupCollapsed && renderTable(list)}
      </div>
    );
  };

  const renderSelectedGroupOnly = () => {
    if (!groupedByAge) return null;
    const groupVal = groupedByAge[selectedGroupView];

    if (!groupVal) {
      return (
        <div className="muted" style={{ marginTop: 12 }}>
          No registrations in this group.
        </div>
      );
    }

    return renderGroupBlock(selectedGroupView, groupVal);
  };

  return (
    <>
      <div className="admin-actions" style={{ marginTop: 14 }}>
        <input
          type="search"
          placeholder="Search name, email or phone"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginLeft: 0 }}
        />

        <select
          value={selectedGroupView}
          onChange={(e) => setSelectedGroupView(e.target.value)}
          style={{ marginLeft: 12, padding: '8px 10px', borderRadius: 8 }}
        >
          <option value="All groups">All groups</option>
          {GROUP_LABELS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>

        <button className="btn btn-ghost" onClick={toggleAllGroups} style={{ marginLeft: 12 }}>
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>

        <select
          value={assignmentFilter}
          onChange={(e) => setAssignmentFilter(e.target.value)}
          style={{ marginLeft: 12, padding: '8px 10px', borderRadius: 8 }}
        >
          <option value="All">All</option>
          <option value="Unassigned">Unassigned</option>
          <option value="Assigned">Assigned</option>
        </select>

        <div className="right-controls">
        </div>
      </div>

      {assignmentsError && (
        <div className="muted" style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
          Assignments fetch failed: {assignmentsError}
        </div>
      )}

      {loading && <div>Loading...</div>}
      {error && <div className="error">{error}</div>}

      {groupBy === 'age' && groupedByAge ? (
        selectedGroupView === 'All groups' ? (
          Object.keys(groupedByAge).map((groupLabel) => renderGroupBlock(groupLabel, groupedByAge[groupLabel]))
        ) : (
          renderSelectedGroupOnly()
        )
      ) : (
        <div style={{ marginTop: 12 }} className="muted">
          No registrations loaded.
        </div>
      )}

      {/* View Registration Modal */}
      {viewModal.open && viewModal.reg ? (
        <div
          className="modal-overlay"
          onClick={() => setViewModal({ open: false, reg: null, payload: null })}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Registration Details</h3>
              <button
                className="btn"
                type="button"
                onClick={() => setViewModal({ open: false, reg: null, payload: null })}
              >
                Close
              </button>
            </div>

            <div style={{ marginTop: 12, border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>
                {getRegFullName(viewModal.reg)}
              </div>
              <div className="muted" style={{ marginTop: 6 }}>
                {viewModal.reg.email || viewModal.reg.Email || ''}{viewModal.reg.phone || viewModal.reg['Phone Number'] ? ` • ${viewModal.reg.phone || viewModal.reg['Phone Number']}` : ''}
              </div>

              <div style={{ marginTop: 12 }}>
                {(() => {
                  const p = viewModal.payload;
                  if (!p) {
                    return <div className="muted">No raw payload found (or it was not valid JSON).</div>;
                  }

                  return (
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <div style={{ fontWeight: 900, marginBottom: 10 }}>Basic Info</div>
                        {renderKeyValueGrid([
                          { label: 'Name', value: `${p.firstName || ''} ${p.lastName || ''}`.trim() },
                          { label: 'DOB', value: fmtDate(p.dob) },
                          { label: 'Age', value: p.age },
                          { label: 'Email', value: p.email },
                          { label: 'Phone', value: fmtPhone(p.phone) }
                        ])}
                      </div>

                      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <div style={{ fontWeight: 900, marginBottom: 10 }}>Housing / Accessibility</div>
                        {renderKeyValueGrid([
                          { label: 'Handicap accessible requested', value: boolYesNo(!!p.handicapAccessible) },
                          { label: 'Rooming with others', value: p.roomingWithOthers },
                          { label: 'Requested roommates', value: p.roommates }
                        ])}
                      </div>

                      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <div style={{ fontWeight: 900, marginBottom: 10 }}>Emergency Contacts</div>
                        {renderKeyValueGrid([
                          { label: 'Emergency #1 Name', value: p.emergency1Name },
                          { label: 'Emergency #1 Phone', value: fmtPhone(p.emergency1Phone) },
                          { label: 'Emergency #2 Name', value: p.emergency2Name },
                          { label: 'Emergency #2 Phone', value: fmtPhone(p.emergency2Phone) }
                        ])}
                      </div>

                      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <div style={{ fontWeight: 900, marginBottom: 10 }}>Medical</div>
                        {renderKeyValueGrid([
                          { label: 'Has allergies', value: p.hasAllergies },
                          { label: 'Allergies', value: p.allergies },
                          { label: 'Allergy acknowledged', value: boolYesNo(!!p.allergyAcknowledged) }
                        ])}
                      </div>

                      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <div style={{ fontWeight: 900, marginBottom: 10 }}>Policies</div>

                        {p.agreements && typeof p.agreements === 'object' ? (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                                <th style={{ padding: '8px 6px' }}>Policy</th>
                                <th style={{ padding: '8px 6px', width: 120 }}>Agreed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.keys(p.agreements)
                                .sort()
                                .map((k) => (
                                  <tr key={k} style={{ borderBottom: '1px solid #f2f2f2' }}>
                                    <td style={{ padding: '8px 6px' }}>{toPolicyLabel(k)}</td>
                                    <td style={{ padding: '8px 6px' }}>{toYesNo(p.agreements[k])}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="muted">No policy agreement data found.</div>
                        )}
                      </div>

                      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                        <button className="btn" type="button" onClick={() => setShowRawJson((v) => !v)}>
                          {showRawJson ? 'Hide raw JSON' : 'Show raw JSON'}
                        </button>

                        {showRawJson ? (
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              margin: '12px 0 0 0',
                              padding: 12,
                              background: '#f7f7f7',
                              border: '1px solid #eee',
                              borderRadius: 10,
                              maxHeight: 420,
                              overflow: 'auto'
                            }}
                          >
                            {JSON.stringify(p, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Print Preview Modal (no popups) */}
      {printModal.open && printModal.reg ? (
        <div
          className="modal-overlay"
          onClick={() => setPrintModal({ open: false, reg: null })}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Print Preview</h3>
              <button className="btn" onClick={() => setPrintModal({ open: false, reg: null })}>
                Close
              </button>
            </div>

            {/* Print layout */}
            <div className="print-area" style={{ marginTop: 12, background: '#fff' }}>
              {/* Top block: name + medical info directly underneath */}
              <div style={{ borderBottom: '1px solid #ddd', paddingBottom: 12 }}>
                <h2 style={{ margin: '0 0 6px 0' }}>{getRegFullName(printModal.reg)}</h2>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                  <div><strong>DOB:</strong> {getRegDOB(printModal.reg)}</div>
                  <div><strong>Allergies:</strong> {getHasAllergies(printModal.reg) ? `${getHasAllergies(printModal.reg)}${getAllergies(printModal.reg) ? ` — ${getAllergies(printModal.reg)}` : ''}` : getAllergies(printModal.reg)}</div>
                  <div><strong>Meds:</strong> {getTakesMeds(printModal.reg)}</div>
                  <div><strong>What meds:</strong> {getMedications(printModal.reg)}</div>
                </div>
              </div>

              {/* Policies table */}
              <div style={{ marginTop: 14, borderTop: '1px solid #ddd', paddingTop: 12 }}>
                <h3 style={{ margin: '0 0 10px 0' }}>Policies</h3>

                {getAgreements(printModal.reg) ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                        <th style={{ padding: '8px 6px' }}>Policy</th>
                        <th style={{ padding: '8px 6px', width: 120 }}>Agreed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(getAgreements(printModal.reg))
                        .sort()
                        .map((k) => (
                          <tr key={k} style={{ borderBottom: '1px solid #f2f2f2' }}>
                            <td style={{ padding: '8px 6px' }}>{toPolicyLabel(k)}</td>
                            <td style={{ padding: '8px 6px' }}>{toYesNo(getAgreements(printModal.reg)[k])}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="muted">No policy agreement data found.</div>
                )}
              </div>

              {/* Signature details AFTER policies */}
              <div style={{ marginTop: 14, borderTop: '1px solid #ddd', paddingTop: 12 }}>
                <h3 style={{ margin: '0 0 10px 0' }}>Electronic Signature</h3>
                <div><strong>Signature:</strong> {getSignature(printModal.reg)}</div>
                <div><strong>Signed by:</strong> {getRelationship(printModal.reg)}</div>
                <div><strong>Date/time signed:</strong> {getSignedAt(printModal.reg)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button className="btn primary" onClick={() => window.print()}>
                Print
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}